import { genericMemo } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useSearch } from "@redotech/react-util/search";
import { TextMatchExpression } from "@redotech/redo-flows-core-schema-common/expressions/text-expression";
import { AdvancedFlowTokenType } from "@redotech/redo-flows-core-schema-common/type-system/data-types/base-types";
import {
  isFieldOfType,
  Schema,
} from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { DeepPartial, DeepPartialExcept } from "@redotech/util/type";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";

const searcher = new Fuse([], { keys: ["value"], threshold: 0.3 });

export const TokenInspector = genericMemo(function TokenInspector({
  selectedField,
  expression,
  setExpression,
}: {
  selectedField: Schema["fields"][string];
  expression: DeepPartialExcept<TextMatchExpression, "field">;
  setExpression: (expression: DeepPartial<TextMatchExpression>) => void;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!expression.type) {
      setExpression({ ...expression, type: "text_match", operator: "equals" });
    }
  }, [expression, setExpression]);

  const [searchText, setSearchText] = useState<string>("");
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const tokenOptionsLoad = useLoad(async (): Promise<
    BadgeRedoListItem<string>[]
  > => {
    if (!isFieldOfType(selectedField, "Token")) {
      return [];
    }
    switch (selectedField.tokenType) {
      case AdvancedFlowTokenType.CUSTOMER_GROUP_ID: {
        const result = await marketingRpcClient.getCustomerGroupsMinimal({});
        return result.map((segment) => ({
          type: "badge",
          id: segment._id,
          value: segment.name,
          badge: { text: segment.name },
        }));
      }
      case AdvancedFlowTokenType.FULFILLMENT_LOCATION_ID: {
        const { locations } = await merchantRpcClient.getLocations(null);
        return locations.map((location) => ({
          type: "badge",
          id: location.externalId,
          value: location.name,
          badge: { text: location.name },
        }));
      }
      case AdvancedFlowTokenType.SIGNUP_FORM_ID: {
        const forms = await marketingRpcClient.getSignupFormOptionsMinimal({});
        return forms.map((form) => ({
          type: "badge",
          id: form.id,
          value: form.name,
          badge: { text: form.name },
        }));
      }
      default:
        return [
          {
            type: "badge",
            id: "1",
            value: "1",
            badge: { text: t("Unsupported tokenType"), color: "error" },
          },
        ];
    }
  }, [marketingRpcClient, merchantRpcClient, selectedField, t]);
  const tokenOptions = useMemo(
    () => tokenOptionsLoad.value ?? [],
    [tokenOptionsLoad.value],
  );

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!expression.matchValues) {
      return [];
    }
    return (
      tokenOptionsLoad.value?.filter((option) =>
        expression.matchValues?.includes(option.id?.toString()),
      ) ?? []
    );
  }, [expression.matchValues, tokenOptionsLoad.value]);

  const searchResults = useSearch(searcher, tokenOptions, searchText);

  return (
    <RedoTagsDropdownInput<string>
      label={t("Value")}
      loading={tokenOptionsLoad.pending}
      options={searchResults}
      searchString={searchText}
      selectedOptions={selectedOptions}
      setSearchString={setSearchText}
      setSelectedOptions={(selectedOptions) => {
        setExpression({
          ...expression,
          matchValues: selectedOptions.map((option) => option.id?.toString()),
        });
      }}
      size="xs"
    />
  );
});
